import React, { useState, useEffect, useCallback, useRef } from "react";

import Paper from "@material-ui/core/Paper";
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Tooltip from '@material-ui/core/Tooltip';
import Skeleton from "@material-ui/lab/Skeleton";

import { makeStyles } from "@material-ui/core/styles";
import { green, grey } from '@material-ui/core/colors';

import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import moment from 'moment';

import Rating from '@material-ui/lab/Rating';
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
    on: {
        color: green[600],
        fontSize: '20px'
    },
    off: {
        color: grey[600],
        fontSize: '20px'
    },
    pointer: {
        cursor: "pointer"
    },
    '@global': {
        '@keyframes pulse': {
            '0%': {
                opacity: 1,
            },
            '50%': {
                opacity: 0.5,
            },
            '100%': {
                opacity: 1,
            },
        },
    },
    clickableNumber: {
        cursor: 'pointer',
        color: theme.palette.primary.main,
        fontWeight: 'bold',
        textDecoration: 'underline',
        '&:hover': {
            color: theme.palette.primary.dark,
            backgroundColor: 'rgba(25, 118, 210, 0.1)',
            borderRadius: '4px',
            padding: '2px 4px',
        },
        transition: 'all 0.2s ease-in-out',
    },
}));

export function RatingBox({ rating }) {
    const ratingTrunc = rating === null ? 0 : Math.trunc(rating);
    return <Rating
        defaultValue={ratingTrunc}
        max={3}
        readOnly
    />
}

export default function TableAttendantsStatus({ 
    attendants, 
    loading, 
    calculateTicketStats, 
    calculateAllUsersStats, 
    periodStart, 
    periodEnd, 
    statsVersion,
    onOpenTicketModal 
}) {
    const classes = useStyles();
    const [localAttendants, setLocalAttendants] = useState([]);
    const [ticketStats, setTicketStats] = useState({});
    const [statsLoading, setStatsLoading] = useState(false);
    const [lastUpdate, setLastUpdate] = useState(Date.now());
    const [currentTime, setCurrentTime] = useState(new Date());
    const [initialStatsCalculated, setInitialStatsCalculated] = useState(false);
    const statsVersionRef = useRef(0);
    const calculateTicketStatsRef = useRef(null);
    const calculateAllUsersStatsRef = useRef(null);
    const attendantsRef = useRef([]);

    // Manter refs atualizados
    useEffect(() => {
        calculateTicketStatsRef.current = calculateTicketStats;
        calculateAllUsersStatsRef.current = calculateAllUsersStats;
        attendantsRef.current = attendants;
    });

    // Sincronizar com os attendants vindos do props
    useEffect(() => {
        setLocalAttendants(attendants);
        setLastUpdate(Date.now());
    }, [attendants]);

    // Calcular estatísticas iniciais apenas uma vez - versão OTIMIZADA
    useEffect(() => {
        if (!calculateAllUsersStats || attendants.length === 0 || initialStatsCalculated) return;
        
        const doInitialCalculation = async () => {
            setStatsLoading(true);
            
            // Uma única chamada para calcular estatísticas de todos os usuários
            const allStats = await calculateAllUsersStats(attendants);
            
            setTicketStats(allStats);
            setStatsLoading(false);
            setInitialStatsCalculated(true);
        };
        
        doInitialCalculation();
    }, [calculateAllUsersStats]); // Só executa quando a função é disponibilizada

    // Recalcular estatísticas quando o botão "Calcular" for clicado - versão OTIMIZADA
    useEffect(() => {
        if (statsVersion > 0 && statsVersion !== statsVersionRef.current) {
            statsVersionRef.current = statsVersion;
            
            const doRecalculation = async () => {
                // Usa as refs para obter valores atuais
                const currentCalculateAllUsersStats = calculateAllUsersStatsRef.current;
                const currentAttendants = attendantsRef.current;
                
                if (!currentCalculateAllUsersStats || currentAttendants.length === 0) return;
                
                setStatsLoading(true);
                
                // Uma única chamada para recalcular estatísticas de todos os usuários
                const allStats = await currentCalculateAllUsersStats(currentAttendants);
                
                setTicketStats(allStats);
                setStatsLoading(false);
            };
            
            doRecalculation();
        }
    }, [statsVersion]); // Mantém apenas statsVersion como dependência

    // Timer para atualizar tempos de sessão ativa em tempo real
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    // Timer para atualizar os tempos localmente
    useEffect(() => {
        const interval = setInterval(() => {
            setLocalAttendants(prevAttendants => 
                prevAttendants.map(attendant => {
                    if (attendant.online && attendant.lastLoginAt) {
                        const now = new Date();
                        const loginTime = new Date(attendant.lastLoginAt);
                        const currentSessionDuration = Math.floor((now - loginTime) / (1000 * 60)); // em minutos
                        
                        return {
                            ...attendant,
                            currentSessionDuration
                        };
                    }
                    return attendant;
                })
            );
            setLastUpdate(Date.now());
        }, 15000); // Atualiza a cada 15 segundos

        return () => clearInterval(interval);
    }, []);

    function formatSessionDuration(attendant) {
        // Se tem lastLoginAt mas não tem lastLogoutAt (ou lastLogoutAt é anterior ao lastLoginAt),
        // significa que ainda está na mesma sessão, mesmo que temporariamente inativo
        if (attendant.lastLoginAt && (!attendant.lastLogoutAt || moment(attendant.lastLogoutAt).isBefore(moment(attendant.lastLoginAt)))) {
            const loginTime = moment(attendant.lastLoginAt);
            const now = moment(currentTime);
            const currentSessionSeconds = now.diff(loginTime, 'seconds');
            
            // Se está online, mostrar com indicador verde
            if (attendant.online) {
                return (
                    <div style={{ 
                        color: '#4caf50', 
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px'
                    }}>
                        <div style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            backgroundColor: '#4caf50',
                            animation: 'pulse 2s infinite'
                        }} />
                        Atual: {formatTimeWithSeconds(currentSessionSeconds)}
                    </div>
                );
            } else {
                // Se está offline (inativo), mostrar com indicador cinza
                return (
                    <div style={{ 
                        color: '#666', 
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px'
                    }}>
                        <div style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            backgroundColor: '#666',
                            animation: 'pulse 2s infinite'
                        }} />
                        Inativo: {formatTimeWithSeconds(currentSessionSeconds)}
                    </div>
                );
            }
        }
        
        // Se tem sessão anterior completa (tem lastLogoutAt posterior ao lastLoginAt)
        if (attendant.lastSessionDuration !== null && attendant.lastSessionDuration !== undefined) {
            return (
                <div style={{ textAlign: 'center' }}>
                    {formatTime(attendant.lastSessionDuration)}
                </div>
            );
        }
        
        return (
            <div style={{ textAlign: 'center' }}>
                N/A
            </div>
        );
    }

    // Função para renderizar números clicáveis
    const renderClickableNumber = (value, ticketIds, title, userId) => {
        if (statsLoading) return "...";
        
        if (value === 0 || !ticketIds || ticketIds.length === 0) {
            return value;
        }
        
        return (
            <span 
                className={classes.clickableNumber}
                onClick={() => onOpenTicketModal && onOpenTicketModal(ticketIds, title, userId)}
                title={`Clique para ver os ${value} tickets`}
            >
                {value}
            </span>
        );
    };

    function renderList() {
        return localAttendants.map((a, k) => {
            // Obter estatísticas de tickets do estado
            const stats = ticketStats[a.id] || {
                ticketsMovimentados: 0,
                aceitos: 0,
                arquivados: 0,
                resolvidos: 0
            };

            return (
                <TableRow key={k}>
                    <TableCell>{a.name}</TableCell>
                    <TableCell align="center">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                            {a.online ? (
                                <div style={{ 
                                    width: '8px', 
                                    height: '8px', 
                                    borderRadius: '50%', 
                                    backgroundColor: '#4caf50' 
                                }} />
                            ) : (
                                <div style={{ 
                                    width: '8px', 
                                    height: '8px', 
                                    borderRadius: '50%', 
                                    backgroundColor: '#f44336' 
                                }} />
                            )}
                            {formatLastOnline(a.lastOnlineAt, a.online)}
                        </div>
                    </TableCell>
                    <TableCell align="center">
                        {formatSessionDuration(a)}
                    </TableCell>
                    <TableCell align="center">
                        {renderClickableNumber(stats.ticketsMovimentados, stats.ticketIds?.movimentados, 'Tickets Movimentados', a.id)}
                    </TableCell>
                    <TableCell align="center">
                        {renderClickableNumber(stats.aceitos, stats.ticketIds?.aceitos, 'Aceitos', a.id)}
                    </TableCell>
                    <TableCell align="center">
                        {renderClickableNumber(stats.arquivados, stats.ticketIds?.arquivados, 'Arquivados', a.id)}
                    </TableCell>
                    <TableCell align="center">
                        {renderClickableNumber(stats.resolvidos, stats.ticketIds?.resolvidos, 'Resolvidos', a.id)}
                    </TableCell>
                </TableRow>
            );
        })
    }

    function formatTime(minutes) {
        // Arredondar para 2 casas decimais para evitar números muito longos
        const roundedMinutes = Math.round(minutes * 100) / 100;
        const hours = Math.floor(roundedMinutes / 60);
        const mins = Math.round(roundedMinutes % 60);
        
        if (hours > 0) {
            return `${hours}h ${mins}m`;
        } else {
            return `${mins}m`;
        }
    }

    function formatTimeWithSeconds(seconds) {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        
        if (hours > 0) {
            return `${hours}h ${minutes}m ${secs}s`;
        } else if (minutes > 0) {
            return `${minutes}m ${secs}s`;
        } else {
            return `${secs}s`;
        }
    }

    function formatLastOnline(dateTime, isOnline) {
        if (!dateTime) {
            return "Nunca conectado";
        }
        
        if (isOnline) {
            return "Online agora";
        }
        
        const now = moment();
        const lastOnline = moment(dateTime);
        const diffInMinutes = now.diff(lastOnline, 'minutes');
        
        if (diffInMinutes < 1) {
            return "Agora";
        } else if (diffInMinutes < 60) {
            return `${diffInMinutes}min atrás`;
        } else if (diffInMinutes < 1440) { // menos de 24 horas
            const hours = Math.floor(diffInMinutes / 60);
            return `${hours}h atrás`;
        } else {
            const days = Math.floor(diffInMinutes / 1440);
            return `${days}d atrás`;
        }
    }

    return (!loading ?
        <TableContainer component={Paper}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>{i18n.t("dashboard.users.name")}</TableCell>
                        <TableCell align="center">{i18n.t("dashboard.users.lastOnline")}</TableCell>
                        <TableCell align="center">{i18n.t("dashboard.users.sessionTime")}</TableCell>
                        <TableCell align="center">Tickets movimentados</TableCell>
                        <TableCell align="center">Aceitos</TableCell>
                        <TableCell align="center">Arquivados</TableCell>
                        <TableCell align="center">Resolvidos</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {renderList()}
                </TableBody>
            </Table>
        </TableContainer>
        : <Skeleton variant="rect" height={150} />
    )
}